import { doc, getDoc, updateDoc, deleteDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase.config';
import { createCompanyIfNeeded } from './companyUtils';
import { CompanyManager } from './enhancedFirebaseUtils';
import UserMigrationUtils from './userMigrationUtils';

/**
 * Process provision request for a new user signing up
 * This function checks for pending provision requests and creates the company
 * @param {string} userId - The user ID
 * @param {object} userData - Additional user data (email, displayName, etc.)
 * @returns {Promise<{success: boolean, companyId?: string, error?: string}>}
 */
export const processProvisionRequest = async (userId, userData = {}) => {
  try {
    console.log('🔍 Checking for provision request for user:', userId);
    
    // Check if Firestore is online
    if (db && db._delegate && db._delegate._settings) {
      // Firestore is available, proceed
    }
    
    // Check if there's a pending provision request
    const provisionRequestRef = doc(db, 'provision_requests', userId);
    let provisionRequestDoc;
    try {
      provisionRequestDoc = await getDoc(provisionRequestRef);
    } catch (error) {
      // Handle offline or network errors gracefully
      if (error.code === 'unavailable' || error.message?.includes('offline') || error.message?.includes('network')) {
        console.log('⚠️ Firestore is offline, skipping provision request check');
        return { success: true, processed: false, offline: true };
      }
      throw error;
    }
    
    if (!provisionRequestDoc.exists()) {
      console.log('📋 No provision request found for user:', userId);
      return { success: true, processed: false };
    }
    
    const provisionData = provisionRequestDoc.data();
    
    // Check if already processed
    if (provisionData.status === 'completed') {
      console.log('✅ Provision request already processed for user:', userId);
      return { success: true, processed: false, companyId: provisionData.companyId };
    }
    
    // Check if status is pending (only process pending requests)
    if (provisionData.status !== 'pending') {
      console.log('⚠️ Provision request is not pending, status:', provisionData.status);
      return { success: true, processed: false };
    }
    
    console.log('🚀 Processing provision request for user:', userId);
    console.log('📋 Company name:', provisionData.companyName);
    
    // Prepare user data for company creation
    const companyUserData = {
      companyName: provisionData.companyName,
      email: userData.email || '',
      displayName: userData.displayName || userData.name || '',
      phone: userData.phone || userData.phoneNumber || '',
      ...userData
    };
    
    // Create the company
    const appId = 'acc-app-e5316'; // Default app ID
    const companyResult = await createCompanyIfNeeded(companyUserData, userId, appId);
    
    if (!companyResult || !companyResult.companyId) {
      throw new Error('Failed to create company from provision request');
    }
    
    const companyId = companyResult.companyId;
    console.log('✅ Company created from provision request:', companyId);
    
    // Ensure user is added to company as owner/employee
    try {
      await UserMigrationUtils.addUserToCompany(userId, companyId, 'owner');
      console.log('✅ User added to company employees:', companyId);
    } catch (employeeError) {
      console.warn('⚠️ Error adding user to company employees (may already exist):', employeeError);
      // Continue - user might already be added
    }
    
    // Ensure user is properly linked to the company in user_companies collection
    // This handles merging with existing companies if any
    try {
      await UserMigrationUtils.createUserCompaniesMapping(userId, companyId);
      console.log('✅ User companies mapping created/updated:', companyId);
      
      // Now ensure active_company is set correctly
      const userCompaniesRef = doc(db, 'user_companies', userId);
      const userCompaniesSnap = await getDoc(userCompaniesRef);
      
      if (userCompaniesSnap.exists()) {
        const existingData = userCompaniesSnap.data();
        // Update active_company if not already set correctly
        if (!existingData.active_company || existingData.active_company.companyId !== companyId) {
          await setDoc(userCompaniesRef, {
            active_company: {
              companyId: companyId,
              type: 'owned',
              switched_at: new Date().toISOString(),
              mpin_verified: false
            },
            last_updated: new Date().toISOString()
          }, { merge: true });
          console.log('✅ Set active company in user_companies:', companyId);
        }
      }
    } catch (mappingError) {
      console.error('❌ Error creating user companies mapping:', mappingError);
      throw new Error(`Failed to link company to user: ${mappingError.message}`);
    }
    
    // Update user profile to include company info if needed
    try {
      await UserMigrationUtils.createOrUpdateUserProfile(userId, {
        email: userData.email || '',
        displayName: userData.displayName || userData.name || '',
        phoneNumber: userData.phone || userData.phoneNumber || '',
        emailVerified: userData.emailVerified || false,
        companyId: companyId
      });
      console.log('✅ User profile updated with company info:', companyId);
    } catch (profileError) {
      console.warn('⚠️ Error updating user profile (non-critical):', profileError);
      // Continue - profile update is not critical for company creation
    }
    
    // Update the provision request status to completed
    try {
      await updateDoc(provisionRequestRef, {
        status: 'completed',
        companyId: companyId,
        processedAt: new Date(),
        processedBy: userId
      });
    } catch (updateError) {
      // Handle offline errors gracefully
      if (updateError.code === 'unavailable' || updateError.message?.includes('offline') || updateError.message?.includes('network')) {
        console.log('⚠️ Firestore is offline, cannot update provision request status');
        // Still return success since company was created
      } else {
        throw updateError;
      }
    }
    
    console.log('✅ Provision request processed successfully:', companyId);
    
    return { 
      success: true, 
      processed: true, 
      companyId: companyId,
      companyData: companyResult
    };
    
  } catch (error) {
    console.error('❌ Error processing provision request:', error);
    
    // Mark provision request as failed (only if not offline error)
    if (error.code !== 'unavailable' && !error.message?.includes('offline') && !error.message?.includes('network')) {
      try {
        const provisionRequestRef = doc(db, 'provision_requests', userId);
        let provisionRequestDoc;
        try {
          provisionRequestDoc = await getDoc(provisionRequestRef);
        } catch (getError) {
          // Skip if offline
          if (getError.code === 'unavailable' || getError.message?.includes('offline')) {
            return { success: false, error: error.message, offline: true };
          }
          throw getError;
        }
        
        if (provisionRequestDoc.exists()) {
          try {
            await updateDoc(provisionRequestRef, {
              status: 'failed',
              error: error.message,
              failedAt: new Date()
            });
          } catch (updateError) {
            // Handle offline errors gracefully
            if (updateError.code !== 'unavailable' && !updateError.message?.includes('offline')) {
              console.error('❌ Failed to update provision request status:', updateError);
            }
          }
        }
      } catch (updateError) {
        // Skip if offline
        if (updateError.code === 'unavailable' || updateError.message?.includes('offline')) {
          return { success: false, error: error.message, offline: true };
        }
        console.error('❌ Failed to update provision request status:', updateError);
      }
    }
    
    return { 
      success: false, 
      processed: false, 
      error: error.message,
      offline: error.code === 'unavailable' || error.message?.includes('offline')
    };
  }
};

/**
 * Get user profile data from user_profiles collection
 * @param {string} userId - The user ID
 * @returns {Promise<object|null>}
 */
export const getUserProfileData = async (userId) => {
  try {
    const userProfileRef = doc(db, 'user_profiles', userId);
    let userProfileDoc;
    try {
      userProfileDoc = await getDoc(userProfileRef);
    } catch (error) {
      // Handle offline or network errors gracefully
      if (error.code === 'unavailable' || error.message?.includes('offline') || error.message?.includes('network')) {
        console.log('⚠️ Firestore is offline, cannot get user profile');
        return null;
      }
      throw error;
    }
    
    if (userProfileDoc.exists()) {
      return userProfileDoc.data();
    }
    
    return null;
  } catch (error) {
    // Only log non-offline errors
    if (error.code !== 'unavailable' && !error.message?.includes('offline')) {
      console.error('Error getting user profile:', error);
    }
    return null;
  }
};

